import { useEffect, useReducer, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import * as Sentry from '@sentry/react';
import { mutate as swrMutate } from 'swr';
import {
  getAuth,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  signInWithEmailAndPassword,
  OAuthProvider,
  type User,
  type UserCredential,
} from 'firebase/auth';
import type { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh';

import axiosInstance, {
  endDocumentSession,
  endpoints,
  isDocumentSessionEnded,
  isSameFirebaseUser,
  bumpSessionRevision,
  readSessionRevision,
} from 'src/utils/axios';
import { sendForgotPasswordLink } from 'src/api/auth';
import { paths } from 'src/routes/paths';
// config
import { clearActingClientStorage } from 'src/sections/customer/acting-client/acting-client-storage';
import {
  AUTH_STORAGE_KEYS,
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from 'src/utils/storage-available';
import { FIREBASE_API } from 'src/config-global';
import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
import { roleForAnalytics } from 'src/utils/check-user-is-admin';
import analytics from 'src/utils/analytics';
//
import { AuthContext } from './auth-context';
import { LoginInterruptedError } from './login-interrupted-error';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);

const AUTH = getAuth(firebaseApp);

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  ENDED = 'ENDED',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.ENDED]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  // NAT-2804: a logout ends in a document navigation. Until it lands the app shows the splash
  // screen (AuthConsumer renders it while loading), not the previous person's page.
  if (action.type === Types.LOGOUT) {
    return {
      user: null,
      loading: true,
    };
  }
  // NAT-2804: this page reloads for a session another tab changed. The splash hides the page until
  // the reload lands; the user stays, so nothing here reads it as this tab's logout.
  if (action.type === Types.ENDED) {
    return { ...state, loading: true };
  }
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

/**
 * The numeric user id carried in the access token's `userId` claim — the same
 * value the backend reports orders under.
 *
 * Read from the token rather than the stored profile because a session that
 * signed in before the login response carried `id` never re-fetches that
 * profile: the refresh interceptor renews tokens only. Those sessions would
 * otherwise identify by email forever, while the server identifies by id.
 * Best-effort by design — a malformed token must never break auth.
 */
function readUserIdClaim(): string | undefined {
  try {
    const token = localStorageGetItem(AUTH_STORAGE_KEYS.accessToken);
    const decoded = token ? decodeTokenPayload(token) : null;
    return decoded?.userId ? String(decoded.userId) : undefined;
  } catch {
    return undefined;
  }
}

/** A JWT's payload, unverified. Null when the token cannot be decoded. Never throws. */
function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(
      decodeURIComponent(
        window
          .atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      ),
    );
    return typeof decoded === 'object' && decoded !== null ? decoded : null;
  } catch {
    return null;
  }
}

/** NAT-2860: the Firebase uid a Firebase ID token was minted for — user_id, else sub. */
function tokenFirebaseUid(token: string): string | null {
  const decoded = decodeTokenPayload(token);
  const uid = decoded?.user_id || decoded?.sub;
  return typeof uid === 'string' && uid ? uid : null;
}

/**
 * Who a stored user record is: the id, else the email. Null when it names no one — an empty
 * string included, so two nameless records can never compare as the same person (NAT-2804).
 */
function userIdentity(user: { id?: unknown; email?: unknown } | null | undefined): string | null {
  const id = user?.id || user?.email;
  return id ? String(id) : null;
}

/**
 * NAT-2804: every trace of a session in this browser — auth keys and the venue selection.
 * NAT-2860: the revision moves first, so a login pending elsewhere never sees the keys gone with
 * the revision still where it began.
 */
function clearSessionStorage() {
  bumpSessionRevision();
  Object.values(AUTH_STORAGE_KEYS).forEach((key) => localStorageRemoveItem(key));
  // NAT-2497: the venue selection is the person's, not the browser's.
  clearActingClientStorage();
}

/**
 * NAT-2804: what a start with no stored user clears — leftovers of an earlier session. Not the
 * access token: with no user stored it may be a public PDF page's pdfToken, which the retired
 * HTML listino page (NAT-2840) wrote there on every render (/pdf/listino-aggiornato?data=…).
 */
function clearStaleSessionKeys() {
  Object.values(AUTH_STORAGE_KEYS)
    .filter((key) => key !== AUTH_STORAGE_KEYS.accessToken)
    .forEach((key) => localStorageRemoveItem(key));
  clearActingClientStorage();
}

/** NAT-2804: how long a logout waits on Firebase's sign-out before it navigates anyway. */
const SIGN_OUT_WAIT_MS = 3000;

/** Whether a user record is stored. One that cannot be read counts as stored: fail closed. */
function hasStoredUser() {
  try {
    return !!localStorageGetItem(AUTH_STORAGE_KEYS.user);
  } catch {
    return true;
  }
}

/**
 * NAT-2860: the session another tab stored while this login was pending, when it is this login's
 * person — the Firebase user this login signed in, and the record the login request answered with
 * — and complete. Null otherwise: no one stored, someone else, or a record that cannot be read.
 */
function adoptableSession(
  storedUserRaw: string | undefined,
  loggedInUser: { id?: unknown; email?: unknown } | undefined,
  firebaseUser: User,
): { userData: Record<string, unknown>; accessToken: string; refreshToken: string } | null {
  if (!storedUserRaw || !isSameFirebaseUser(storedUserRaw, firebaseUser)) return null;
  let stored: Record<string, unknown>;
  try {
    stored = JSON.parse(storedUserRaw);
  } catch {
    return null;
  }
  const identity = userIdentity(stored);
  if (identity === null || identity !== userIdentity(loggedInUser)) return null;
  const accessToken = localStorageGetItem(AUTH_STORAGE_KEYS.accessToken);
  if (!accessToken) return null;
  // The token must be this login's person too, whatever order the keys were read in: a token
  // minted for another Firebase user — or one that cannot be read — is not adopted.
  if (tokenFirebaseUid(accessToken) !== firebaseUser.uid) return null;
  return {
    userData: stored,
    accessToken,
    refreshToken: localStorageGetItem(AUTH_STORAGE_KEYS.refreshToken) ?? '',
  };
}

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // LOGOUT
  // NAT-2804: the boundary between two people on this tab is a full document navigation. It ends
  // every request still in flight, the refresh queue and its retries, and SWR's in-memory cache
  // and de-duplication state — none of which a client-side route change touches. Before it lands
  // the session fails closed: storage is cleared synchronously, before Firebase is asked anything,
  // and the navigation happens whether or not Firebase confirms the sign-out. `replace`, not
  // `assign`: Back must not reopen the previous person's page, whose URL the auth guard would then
  // carry into the next person's login as `returnTo`. Never rejects; callers navigate nothing.
  // The wait on Firebase is bounded, and the navigation runs whatever throws before it.
  const logout = useCallback(async () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      // First, synchronously: from here no request leaves this document and no refresh touches
      // storage, whatever another tab stores before the navigation lands.
      endDocumentSession();
      dispatch({ type: Types.LOGOUT });
      clearSessionStorage();
      // NAT-2783: SWR's cache is global and in memory. The navigation below drops it; emptying it
      // now also covers the moment until then.
      swrMutate(() => true, undefined, { revalidate: false }).catch(() => undefined);
      await Promise.race([
        signOut(AUTH),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, SIGN_OUT_WAIT_MS);
        }),
      ]);
    } catch (error) {
      try {
        Sentry.captureException(error);
      } catch {
        // Reporting must not stop the logout.
      }
    } finally {
      clearTimeout(timer);
      window.location.replace(paths.login);
    }
  }, []);

  const initialize = useCallback(async () => {
    try {
      const storedUserRaw = localStorageGetItem(AUTH_STORAGE_KEYS.user);
      if (!storedUserRaw) {
        // NAT-2804: no session — possibly a public page. Nothing to sign out, nowhere to go.
        clearStaleSessionKeys();
        dispatch({ type: Types.INITIAL, payload: { user: null } });
        return;
      }

      const accessToken = localStorageGetItem(AUTH_STORAGE_KEYS.accessToken);
      const refreshToken = localStorageGetItem(AUTH_STORAGE_KEYS.refreshToken);
      const userData = JSON.parse(storedUserRaw);

      // NAT-2804, fail closed: a stored user that names no one, or no access token, is no
      // session. Every login stores the server's record, which carries both id and email.
      if (accessToken && userIdentity(userData) !== null) {
        dispatch({
          type: Types.INITIAL,
          payload: { user: { ...userData, accessToken, refreshToken } },
        });
        return;
      }

      // An invalid stored session is cleared and Firebase signed out — without navigating: the
      // app renders signed out and the guards route from there. A navigation here would loop on
      // the public PDF page, which writes its (possibly empty) token on every render.
      clearSessionStorage();
      dispatch({ type: Types.INITIAL, payload: { user: null } });
      signOut(AUTH).catch((error) => Sentry.captureException(error));
    } catch {
      // Fail closed: a start that cannot read its session is signed out. With no user stored the
      // access token is the public PDF page's pdfToken, and it stays.
      try {
        if (hasStoredUser()) clearSessionStorage();
        else clearStaleSessionKeys();
      } finally {
        dispatch({ type: Types.INITIAL, payload: { user: null } });
      }
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // NAT-2783: localStorage is shared between tabs, SWR's cache and the role-derived rendering are
  // not. If another tab signs a different person in (or out), this tab would keep showing the
  // previous person's cached data under the new role. A reload drops every in-memory cache.
  // A rewrite of the same person (token refresh) must not reload.
  // NAT-2804, fail closed: a storage clear in another tab (key null) and a session whose stored
  // user names no one both reload — this tab cannot confirm it still belongs to the same person.
  // NAT-2804: until the reload lands this page could still send a request with the other tab's new
  // token, or refresh and replay a late 401 under it. The reload ends this page's session first and
  // hides the page; storage is left alone — it holds the other tab's session.
  const heldSessionRef = useRef<{ identity: string | null } | null>(null);
  heldSessionRef.current = state.user ? { identity: userIdentity(state.user) } : null;
  const endAndReload = useCallback(() => {
    endDocumentSession();
    dispatch({ type: Types.ENDED });
    window.location.reload();
  }, []);
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      const held = heldSessionRef.current;
      if (!held) return;
      // Sessions live in localStorage; a sessionStorage change in another tab is not one.
      if (event.storageArea !== window.localStorage) return;
      if (event.key === null) {
        endAndReload();
        return;
      }
      if (event.key !== AUTH_STORAGE_KEYS.user) return;
      let next: string | null = null;
      try {
        next = event.newValue ? userIdentity(JSON.parse(event.newValue)) : null;
      } catch {
        next = null;
      }
      if (held.identity === null || next !== held.identity) endAndReload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [endAndReload]);

  // NAT-2804: Back after a logout can restore the old document from the back-forward cache, frozen
  // as it was — the previous person's screen and memory, and no storage events received meanwhile.
  // A restored page whose session ended, or whose stored person is not the one it holds (no one
  // included), reloads.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      let stored: string | null = null;
      try {
        const raw = localStorageGetItem(AUTH_STORAGE_KEYS.user);
        stored = raw ? userIdentity(JSON.parse(raw)) : null;
      } catch {
        stored = null;
      }
      const held = heldSessionRef.current?.identity ?? null;
      if (isDocumentSessionEnded() || stored !== held) endAndReload();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [endAndReload]);

  const identifiedUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const { user } = state;
    if (!user) {
      if (identifiedUserIdRef.current) {
        analytics.reset();
        identifiedUserIdRef.current = null;
      }
      return;
    }
    if (user.role === CONSTANT_VARIABLES.ADMIN) return;

    // The token claim first, then the stored profile. Both resolve to the same
    // numeric user id the backend reports orders under — but the claim is
    // present for sessions that logged in before `id` was added to the login
    // response, and those sessions never re-fetch their profile. Falling back
    // to the email would key them to a second Mixpanel profile that no server
    // event ever lands on, silently splitting every funnel. NAT-2165.
    const distinctId = String(readUserIdClaim() ?? user.id ?? user.uid ?? user.email ?? '');
    if (!distinctId || identifiedUserIdRef.current === distinctId) return;

    const company = user.companyName || user.company || user.client_name || user.clientName;
    const agent = user.agentName || user.agent_name || user.agent;
    const { region, province } = user;

    const traits: Record<string, unknown> = {
      email: user.email,
      name:
        user.fullName ||
        [user.firstName || user.first_name, user.lastName || user.last_name]
          .filter(Boolean)
          .join(' ') ||
        user.name ||
        user.displayName,
      role: user.role,
    };
    if (company) traits.company = company;
    if (region) traits.region = region;
    if (province) traits.province = province;
    if (agent) traits.agent = agent;

    analytics.identify(distinctId, traits);
    analytics.register({ user_role: roleForAnalytics(user.role) });
    identifiedUserIdRef.current = distinctId;
  }, [state]);

  // Sentry identity — NAT-2177.
  //
  // Deliberately separate from the Mixpanel effect above rather than folded into it:
  //  - that effect skips admins (a product-analytics decision), but Sentry must attribute them.
  //    The worst unresolved FE issue, the stale-chunk failure on the order edit page, is an
  //    admin-only surface — skipping admins would leave it reading "Users: 0" forever.
  //  - it also falls back to the email as a distinct id. Sentry gets the id or nothing, so an
  //    address can never reach it that way. See NAT-1784 for the related Replay exposure.
  //
  // The token claim is the ONLY source, with no fallback to user.id / user.uid. Those are not
  // populated by the login response, and user.uid would be a Firebase uid — a different identity
  // space from the numeric userId the backend attributes against, so a session that fell back to
  // it would silently stop correlating with server-side events. No id means no attribution,
  // which is the honest outcome.
  const sentryUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const { user } = state;
    const nextId = user ? (readUserIdClaim() ?? null) : null;

    if (sentryUserIdRef.current === nextId) return;
    Sentry.setUser(nextId ? { id: nextId } : null);
    sentryUserIdRef.current = nextId;
  }, [state]);

  // LOGIN
  // NAT-2804: one login at a time. A login started while another is pending is refused, and one
  // overtaken by a logout writes nothing; both reject, so the login view runs its failure path.
  // The refresh interceptor's Firebase identity check is the backstop behind this.
  // NAT-2828: another tab can sign someone in, or out, while this login is pending — and a
  // signed-out tab ignores other tabs' storage events. So the login writes only if, at the moment
  // it writes, all three hold: Firebase (shared by every tab) still holds the person it signed in,
  // the stored user is the one stored when it began, and the session revision (bumped by every
  // login and logout, so a login and a logout elsewhere cannot hide) is the one read when it
  // began. Otherwise it rejects and writes nothing. It does not sign out of Firebase: that would
  // sign the other tab's person out as well.
  // NAT-2860: one exception — the stored session moved to this login's own person (the same
  // person signing in from two tabs) while Firebase still holds them: the login adopts it as
  // stored. A refusal for another tab's doing rejects with LoginInterruptedError.
  const loginPendingRef = useRef(false);
  const runLogin = useCallback(async (signIn: () => Promise<UserCredential>) => {
    if (loginPendingRef.current) throw new Error('A login is already in progress');
    loginPendingRef.current = true;
    try {
      const storedUserAtStart = localStorageGetItem(AUTH_STORAGE_KEYS.user);
      const revisionAtStart = readSessionRevision();
      const { user: firebaseUser } = await signIn();
      if (!firebaseUser) return;

      const token: { token: string } = { token: await firebaseUser.getIdToken() };
      // NAT-2823: the login request carries a Firebase id token, not the stored session. A 401 on
      // it is the login's answer, never a reason to refresh whatever session another tab stored.
      const loginRequestConfig: AxiosAuthRefreshRequestConfig = { skipAuthRefresh: true };
      const userDetails = await axiosInstance.post(
        endpoints.auth.firebase.loginWithPassword,
        token,
        loginRequestConfig,
      );
      // refresh after claims are set
      const freshAccessToken = await firebaseUser.getIdToken(true);
      // NAT-2804: a logout, or a refresh that failed closed, ended this page's session meanwhile.
      if (isDocumentSessionEnded())
        throw new Error('The session ended while the login was pending');
      // NAT-2860: the refusals below are another tab's doing, not the credentials'.
      if (!AUTH.currentUser || AUTH.currentUser.uid !== firebaseUser.uid)
        throw new LoginInterruptedError(
          'Firebase signed someone else in while the login was pending',
        );

      const { userData: loggedInUser } = userDetails?.data?.data || {};
      let userData = loggedInUser;
      let accessToken = freshAccessToken;
      let { refreshToken } = firebaseUser;
      const storedUserNow = localStorageGetItem(AUTH_STORAGE_KEYS.user);
      // A login and a logout elsewhere can leave the stored user as it was; the revision moved.
      if (storedUserNow !== storedUserAtStart || readSessionRevision() !== revisionAtStart) {
        // NAT-2860: the same person signed in from another tab meanwhile, and Firebase still holds
        // them. That session is this login's too: adopt it, write nothing. Anything else refuses.
        const adopted = adoptableSession(storedUserNow, loggedInUser, firebaseUser);
        if (!adopted)
          throw new LoginInterruptedError(
            'Another tab signed in or out while the login was pending',
          );
        ({ userData, accessToken, refreshToken } = adopted);
      } else {
        localStorageSetItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
        localStorageSetItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken);
        localStorageSetItem(AUTH_STORAGE_KEYS.user, JSON.stringify(userData));
        bumpSessionRevision();
      }

      dispatch({
        type: Types.LOGIN,
        payload: { user: { ...userData, accessToken, refreshToken } },
      });

      // Tracked only once the session is persisted and dispatched. These login
      // functions have no local catch, so a throw out of the SDK here would
      // reach the login view and be reported to the customer as "invalid email
      // or password" while they are, in fact, signed in.
      if (userData?.role && userData.role !== CONSTANT_VARIABLES.ADMIN) {
        // Register BEFORE tracking. This event fires synchronously here, while
        // the effect below only re-registers on the next commit — and on this
        // load the visitor was signed out, so the super-property still says
        // `anonymous`. That is why all nine recorded sign-ins carried
        // `role: customer` and `user_role: anonymous` side by side. NAT-2318.
        analytics.register({ user_role: roleForAnalytics(userData.role) });
        analytics.track('user_logged_in', { role: userData.role });
      }
    } finally {
      loginPendingRef.current = false;
    }
  }, []);

  const loginWithPassword = useCallback(
    (email: string, password: string) =>
      runLogin(() => signInWithEmailAndPassword(AUTH, email, password)),
    [runLogin],
  );

  const loginWithGoogle = useCallback(
    () => runLogin(() => signInWithPopup(AUTH, new GoogleAuthProvider())),
    [runLogin],
  );

  const loginWithApple = useCallback(
    () =>
      runLogin(() => {
        const appleProvider = new OAuthProvider('apple.com');
        appleProvider.addScope('email');
        appleProvider.addScope('name');
        return signInWithPopup(AUTH, appleProvider);
      }),
    [runLogin],
  );

  const loginWithGithub = useCallback(async () => {
    const provider = new GithubAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithTwitter = useCallback(async () => {
    const provider = new TwitterAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await sendForgotPasswordLink({ email });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      loginWithPassword,
      logout,
      forgotPassword,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
      loginWithApple,
    }),
    [
      status,
      state.user,
      //
      loginWithPassword,
      logout,
      forgotPassword,
      loginWithGithub,
      loginWithGoogle,
      loginWithTwitter,
      loginWithApple,
    ],
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
